// eslint-disable-next-line no-unused-vars
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { useTable, useFilters, useGlobalFilter, usePagination, useSortBy, useAsyncDebounce } from 'react-table';
import {
    closestCorners,
    DndContext,
    DragOverlay,
    KeyboardSensor,
    MouseSensor,
    TouchSensor,
    useSensor,
    useSensors
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { restrictToVerticalAxis, snapCenterToCursor } from '@dnd-kit/modifiers';

import { DataTableActions } from './components/DataTableActions';
import { DataTableContainer } from './components/DataTableContainer';
import { DataTableControlDeck } from './components/DataTableControlDeck';
import { DataTableHeader } from './components/DataTableHeader';
import { TableBody } from '../Table/components/TableBody';
import { TableEmptyState } from '../Table/components/TableEmptyState';
import { TableHeader } from '../Table/components/TableHeader';
import { TableRow, TableRowDragSort } from '../Table/components/TableRow';
import { TableCell } from '../Table/components/TableCell';
import { MaterialIcon } from '../MaterialIcon';

const ColumnHeader = styled.span`
    color: ${({ filtering }) => (filtering ? '#337ab7 !important' : '#333333')};
    ${({ right }) =>
        right &&
        css`
            flex: 1;
            flex-direction: row-reverse !important;
        `}
`;

const DragOverlayInner = styled.div`
    cursor: grabbing !important;
    padding: 5px 12px;
    background: #fff;
    border: 2px solid #a2c3df;
    /* :hover {
        cursor: grabbing !important;
    } */
`;

const RenderDataTableCell = ({ datum, ...props }) => {
    return (
        <TableCell
            actions={typeof datum.column.DataTableActions !== 'undefined'}
            style={{
                ...(datum.column?.renderOptions?.columnStyles || {})
            }}
        >
            {typeof datum.column.renderOptions !== 'undefined' ? (
                datum.column.renderOptions?.values[datum.value]
            ) : typeof datum.column.DataTableActions !== 'undefined' ? (
                <DataTableActions actions={datum.value} />
            ) : (
                datum.render('Cell')
            )}
        </TableCell>
    );
};

RenderDataTableCell.propTypes = {
    datum: PropTypes.object
};

const TableWrapperSortable = ({ data, dndContextProps, rows, children }) => {
    const sensors = useSensors(useSensor(MouseSensor, {}), useSensor(TouchSensor, {}), useSensor(KeyboardSensor, {}));
    const activeRow = React.useMemo(() => {
        return rows.find((row) => dndContextProps?.getDraggableId(row.original) === dndContextProps?.activeId);
    }, [data, dndContextProps?.activeId]);

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            modifiers={[restrictToVerticalAxis]}
            onDragEnd={dndContextProps.onDragEnd}
            onDragStart={dndContextProps.onDragStart}
            onDragCancel={dndContextProps.onDragCancel}
        >
            <SortableContext
                items={data.map((item) => dndContextProps.getDraggableId(item))}
                strategy={verticalListSortingStrategy}
            >
                {children}
            </SortableContext>
            {createPortal(
                <DragOverlay zIndex={9999}>
                    {activeRow && (
                        <DragOverlayInner>
                            <table style={{ width: '100%', pointerEvents: 'none' }}>
                                <tbody>
                                    <tr>
                                        {activeRow.cells.map((cell, key) => (
                                            // eslint-disable-next-line react/jsx-key
                                            <RenderDataTableCell datum={cell} key={key} />
                                        ))}
                                    </tr>
                                </tbody>
                            </table>
                        </DragOverlayInner>
                    )}
                </DragOverlay>,
                document.body
            )}
        </DndContext>
    );
};
const TableWrapperDefault = ({ children }) => {
    return <React.Fragment>{children}</React.Fragment>;
};

const TableWrapperProps = {
    data: PropTypes.array,
    rows: PropTypes.array,
    children: PropTypes.node,
    dndContextProps: PropTypes.shape({
        onDragEnd: PropTypes.func,
        onDragStart: PropTypes.func,
        onDragCancel: PropTypes.func,
        getDraggableId: PropTypes.func,
        activeId: PropTypes.string
    })
};
TableWrapperSortable.propTypes = TableWrapperProps;
TableWrapperDefault.propTypes = TableWrapperProps;

/**
 * Based on v7.0.0+ of `react-table`
 * ([documentation](https://github.com/tannerlinsley/react-table)),
 * the `DataTable` component is designed to provide the UI components with a
 * basic implementation of the various utilities and toolings provided by
 * `react-table`, including sorting, filtering, pagination, and default
 * layout recommendations.
 *
 * In most cases, this basic implementation should be overkill, however, as it
 * is based off an implementation with hooks, performance should scale with
 * the complexity of _your_ usage of this components, and that of your dataset.
 */

export const DataTable = ({
    columns,
    controlButtons,
    hiddenControls,
    data,
    dndContextProps,
    entityLabels,
    error,
    filterPlaceholder,
    fullWidth,
    headerButtons,
    id,
    label,
    loading,
    numRowsInitialValue,
    paginated,
    setTableState,
    showControlDeck,
    showHeader,
    showTableHeader,
    tableState,
    overflowX,
    sortableRows,
    rowClickAction,
    tableProps,
    emptyStateProps,
    ...props
}) => {
    // const [showFilterColumnOptions, setShowFilterColumnOptions] = useState(false);
    const [filterColumn, setFilterColumn] = useState(null);
    // const filterableColumns = React.useMemo(() => columns.filter((column) => column.filterable), [columns]);
    const reactTableArgs = [
        {
            data,
            columns,
            initialState: tableState
        },
        useFilters,
        useGlobalFilter,
        useSortBy,
        usePagination // Not alphashorted due to exception if placed before useSortBy
    ];

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        state,
        setGlobalFilter,
        setFilter,
        setAllFilters,
        pageOptions,
        page,
        gotoPage,
        previousPage,
        canPreviousPage,
        nextPage,
        canNextPage,
        setPageSize
    } = useTable(...reactTableArgs);
    const isDragSortableRows = sortableRows && typeof dndContextProps !== 'undefined';
    const TableContextWrapper = isDragSortableRows ? TableWrapperSortable : TableWrapperDefault;

    useEffect(() => {
        typeof setTableState === 'function' && setTableState(state);
    });

    const dataTableProps = {
        displayLabel: !!(typeof label && label),
        fullWidth,
        id,
        label,
        ...tableProps
    };

    const dataTableHeaderProps = {
        displayLabel: !!(typeof label && label),
        filterPlaceholder,
        headerButtons,
        id,
        label
    };

    const paginationProps = {
        backward: previousPage,
        backwardDisabled: !canPreviousPage,
        forward: nextPage,
        forwardDisabled: !canNextPage,
        goToPage: (v) => gotoPage(Number(v)), // Force number type
        numPages: pageOptions.length || 1,
        numRows: state.pageSize || numRowsInitialValue,
        pageIndex: state.pageIndex || 0,
        setNumRows: (v) => setPageSize(Number(v)) // Force number type
    };

    const controlDeckProps = {
        buttons: controlButtons,
        pagination: paginated ? paginationProps : false,
        hiddenControls: hiddenControls,
        ...entityLabels
    };

    const setGlobalFilterAndResetPageIndex = (v) => {
        if (filterColumn) {
            setFilter(filterColumn.accessor, v);
            setGlobalFilter('');
        } else {
            setAllFilters([]);
            setGlobalFilter(v);
        }
        gotoPage(0);
    };

    const onFilterChangeDebounced = useAsyncDebounce((v) => {
        if (paginated) {
            setGlobalFilterAndResetPageIndex(v);
        } else {
            setGlobalFilter(v);
        }
    }, 200);

    return (
        <TableContextWrapper data={data} dndContextProps={dndContextProps} rows={rows}>
            {showHeader && (
                <DataTableHeader
                    {...dataTableHeaderProps}
                    globalFilterContext={filterColumn ? state.filters?.[0]?.value : state.globalFilter}
                    filterColumn={filterColumn}
                    setFilterColumn={setFilterColumn}
                    columns={columns}
                    setGlobalFilter={setGlobalFilter}
                    onFilterChange={onFilterChangeDebounced}
                    filterWidth={'50%'}
                    maxFilterWidth={'28rem'}
                />
            )}
            <DataTableContainer
                {...dataTableProps}
                {...getTableProps()}
                {...(overflowX ? { overflowX } : {})}
                {...(!showHeader ? { borderTop: '1px solid rgba(67,90,111,0.14)' } : {})}
            >
                {showTableHeader && (
                    <TableHeader>
                        {headerGroups.map((headerGroup) => (
                            // eslint-disable-next-line react/jsx-key
                            <TableRow {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map((col) => (
                                    // eslint-disable-next-line react/jsx-key
                                    <TableCell {...col.getHeaderProps(col.getSortByToggleProps())}>
                                        <ColumnHeader
                                            filtering={filterColumn && filterColumn.label === col.Header}
                                            right={col.headerRightAlign}
                                        >
                                            {col.render('Header')}
                                        </ColumnHeader>
                                        {col.isSorted ? (
                                            col.isSortedDesc ? (
                                                <MaterialIcon
                                                    icon="arrow_drop_down"
                                                    intent="info"
                                                    size={18}
                                                    // TODO: add a11y for this icon
                                                />
                                            ) : (
                                                <MaterialIcon
                                                    icon="arrow_drop_up"
                                                    intent="info"
                                                    size={18}
                                                    // TODO: add a11y for this icon
                                                />
                                            )
                                        ) : (
                                            ``
                                        )}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableHeader>
                )}
                <TableBody loading={loading} {...getTableBodyProps()}>
                    {!error && rows.length ? (
                        (paginated && page ? page : rows).map((row, i) => {
                            prepareRow(row);

                            let customRowProps = {};

                            if (typeof row?.original?.DeferredSaveIndicator !== 'undefined') {
                                customRowProps.dsi = row.original.DeferredSaveIndicator;
                            }

                            if (typeof row?.original?.onRowClick !== 'undefined') {
                                customRowProps.onClick = row.original.onRowClick;
                            }

                            if (row?.original?.selected) {
                                customRowProps.selected = true;
                            }

                            return isDragSortableRows ? (
                                <TableRowDragSort
                                    id={dndContextProps?.getDraggableId(row.original)}
                                    {...customRowProps}
                                    {...row.getRowProps()}
                                    cells={row.cells}
                                />
                            ) : (
                                // eslint-disable-next-line react/jsx-key
                                <TableRow
                                    {...customRowProps}
                                    {...row.getRowProps()}
                                    {...(rowClickAction
                                        ? {
                                              onClick: () => rowClickAction(row.original)
                                          }
                                        : {})}
                                >
                                    {row.cells.map((cell) => (
                                        // eslint-disable-next-line react/jsx-key
                                        <RenderDataTableCell datum={cell} {...cell.getCellProps()} />
                                    ))}
                                </TableRow>
                            );
                        })
                    ) : (
                        <TableRow>
                            <TableEmptyState
                                cols={columns.length}
                                error={error}
                                label={entityLabels?.plural || `data`}
                                query={state?.globalFilter || ``}
                                customlabel={emptyStateProps.customLabel}
                                labelfontsize={emptyStateProps.labelFontSize}
                                imagesize={emptyStateProps.imageSize}
                            />
                        </TableRow>
                    )}
                </TableBody>
            </DataTableContainer>
            {showControlDeck && <DataTableControlDeck {...controlDeckProps} />}
        </TableContextWrapper>
    );
};

DataTable.propTypes = {
    /**
     * A **memoized** array of `Column` definitions.
     *
     * Refer to the [documentation](https://github.com/tannerlinsley/react-table/blob/master/docs/api/useTable.md#table-options) for more information.
     */
    columns: PropTypes.array.isRequired,
    /**
     * Pass an array of `<Button />` components for rendering in the control
     * deck.
     */
    controlButtons: PropTypes.array,
    cursor: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /**
     * A **memoized** array of data to be mapped into the table.
     *
     * Refer to the [documentation](https://github.com/tannerlinsley/react-table/blob/master/docs/api/useTable.md#table-options) for more information.
     */
    data: PropTypes.array.isRequired,
    /**
     * If tables rows are sortable with dnd-kit then event handlers are supplied via dndContextProps
     */
    dndContextProps: PropTypes.shape({
        onDragEnd: PropTypes.func,
        onDragStart: PropTypes.func,
        onDragCancel: PropTypes.func,
        getDraggableId: PropTypes.func,
        activeId: PropTypes.string
    }),
    /**
     * Props for table empty state component
     */
    emptyStateProps: PropTypes.object,
    /**
     * Defines the labels to be used when describing the data represented by
     * this table. Used in the empty state, show X of Y dropdown, and various
     * autogenerated `title=""` parameters.
     */
    entityLabels: PropTypes.object,
    /**
     * _Reserved for future use_; to indicate an error state in loading of
     * table data, or a child element rendering error.
     */
    error: PropTypes.oneOfType([PropTypes.bool, PropTypes.object, PropTypes.string]),
    /**
     * Requires `showHeader`. Sets the placeholder value for the table's filter.
     */
    filterPlaceholder: PropTypes.string,
    /**
     * Forces the table to stretch to fill the parent element's available space.
     */
    fullWidth: PropTypes.bool,
    /**
     * Optional actions rendered between the table heading and search field.
     */
    headerButtons: PropTypes.array,
    /**
     * Determines which table controls are visible (eg. page size and pagination buttons)
     */
    hiddenControls: PropTypes.object,
    /**
     * For accessibility requirements, provide a unique ID for your table.
     *
     * This value is also used to generate unique keys in various mappings.
     */
    id: PropTypes.string.isRequired,
    /**
     * Requires `showHeader`. Displays a heading above the table.
     */
    label: PropTypes.string,
    /**
     * Set a loading state on the table render, and display a loading spinner
     * over the rendered table rows.
     */
    loading: PropTypes.bool,
    /**
     * Requires `paginated`. Sets the initial number of rows to render.
     */
    numRowsInitialValue: PropTypes.number,
    /**
     * Enables the pagination controls for the table.
     */
    paginated: PropTypes.bool,
    setTableState: PropTypes.func,
    /**
     * Boolean to indicate if select action should be invoked on click of whole row
     */
    rowClickAction: PropTypes.func,
    /**
     * Render the control deck below the table body.
     */
    showControlDeck: PropTypes.bool,
    /**
     * Display the table's label as a heading, and the global filter.
     */
    showHeader: PropTypes.bool,
    showTableHeader: PropTypes.bool,
    sortableRows: PropTypes.bool,
    tableState: PropTypes.object,
    tableProps: PropTypes.object,
    overflowX: PropTypes.string
};

DataTable.defaultProps = {
    columns: [],
    controlButtons: [],
    cursor: 0,
    data: [],
    entityLabels: {
        singular: `entry`,
        plural: `entries`
    },
    error: false,
    filterPlaceholder: `Search...`,
    fullWidth: true,
    headerButtons: [],
    loading: false,
    numRowsInitialValue: 10,
    paginated: false,
    showControlDeck: true,
    showHeader: false,
    showTableHeader: true,
    hiddenControls: {},
    tableState: {},
    emptyStateProps: {}
};
